import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { Collapse } from 'reactstrap';

import './LinksGroup.css';

interface ChildLink {
  name: string;
  link: string;
  icon?: (props: any) => React.ReactElement;
}

interface LinksGroupProps {
  header: React.ReactNode;
  headerLink?: string;
  childrenLinks?: ChildLink[];
  iconComponent?: (props: any) => React.ReactElement;
  glyph?: string;
  className?: string;
  children?: React.ReactNode;
  showIconOnly?: boolean;
}

const LinksGroup: React.FC<LinksGroupProps> = ({
  header,
  headerLink = null,
  childrenLinks = null,
  iconComponent,
  glyph = null,
  className = '',
  children,
  showIconOnly,
}) => {
  const location = useLocation();
  // Auto-expand if any child link is active
  const hasActiveChild = childrenLinks?.some(child => location.pathname === child.link) || false;
  const [isOpen, setIsOpen] = useState(hasActiveChild);
  
  // Update isOpen when location changes
  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  if (!childrenLinks) {
    return (
      <li className={cx('headerLink', className)}>
        <NavLink to={headerLink || ''} className={({ isActive }) => isActive ? 'headerLinkActive' : ''}>
          <div>
            {iconComponent && React.createElement(iconComponent, {
              className: "sidebar-icon mx-2",
              style: { fontSize: showIconOnly ? '2rem' : '1.5rem' }
            })}
            {!showIconOnly && <span style={{ direction: 'ltr', paddingRight: '10px' }}>{header}</span>}
          </div>
        </NavLink>
      </li>
    );
  }

  const isAnyChildActive = childrenLinks.some(child => location.pathname === child.link);

  return (
    <li className={cx('headerLink', className)}>
      <a
        className={cx('sidebar-link', { 'active': isAnyChildActive || isOpen, 'justify-content-center': showIconOnly })}
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        {iconComponent && React.createElement(iconComponent, {
          className: "sidebar-icon mx-2",
          style: { fontSize: showIconOnly ? '2rem' : '1.5rem' }
        })}
        {!showIconOnly && <span className="text-truncate">{header}</span>}
        {!showIconOnly && (
          <b className={cx('fa fa-angle-left arrow', 'arrow', { 'arrowActive': isOpen })} style={{ marginLeft: 'auto' }} />
        )}
      </a>
      <Collapse className="panel" isOpen={isOpen}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {childrenLinks.map((child) => {
            const childActive = location.pathname === child.link;
            return (
              <li key={child.link}>
                <NavLink
                  to={child.link}
                  className={({ isActive }) => cx('sidebar-link', 'sidebar-child-link', { 'active': isActive || childActive })}
                  onClick={() => setIsOpen(true)}
                >
                  {child.icon && React.createElement(child.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: showIconOnly ? '1.5rem' : '1rem' }
                  })}
                  {!showIconOnly && <span className="text-truncate">{child.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </Collapse>
      {children}
    </li>
  );
};

export default LinksGroup;
